import {
  credentialHeaderNames,
  credentialHeadersFor,
  type CredentialSource,
} from './credentials.ts';
import { ApiError, ApiNetworkError } from './errors.ts';
import type { ErrorEnvelope } from './generated/models.ts';
import type {
  DownloadArgs,
  NonJsonOperationId,
  OperationDescriptor,
  OperationId,
  OperationResponse,
  RawBody,
  RequestArgs,
} from './operation.ts';

/**
 * The transport. It knows how to turn a generated operation descriptor into an
 * HTTP call and back; it knows nothing about any particular endpoint, and no
 * path, body or response shape is written down here.
 *
 * It does not retry. Only the caller knows whether a request was safe to
 * repeat — `ApiError.retryable` is what it decides from.
 */

export type HeaderSource =
  | Record<string, string>
  | (() => Record<string, string> | Promise<Record<string, string>>);

export interface ApiClientOptions {
  /** Base URL of blossom-rest-api, with or without a trailing slash. */
  baseUrl: string;

  /**
   * Headers applied to every request. **Not** where authentication goes — that
   * is `credentials`, which chooses per operation.
   *
   * Any header a credential could occupy is dropped from here before the
   * selected credential is applied: `authorization`, and every scheme with a
   * header of its own — `x-terminal-lease` today. Being overwritten was not
   * enough on its own, because the selected credential wins by *name*, so it
   * displaced a parked credential only where the two shared a header name, and
   * this document's two schemes deliberately do not. A lease parked here rode
   * along on all 21 back-office operations and on the unauthenticated sign-in.
   *
   * Resolved per request, so a value that changes during the client's life is
   * picked up without rebuilding it. A credential is not that value: put a
   * renewing lease or a session in `credentials`, whose providers are resolved
   * per request for exactly this reason.
   */
  headers?: HeaderSource;

  /**
   * The credentials this client can hold, one provider per security scheme.
   *
   * Which one is sent is decided from the operation's own `security`, in one
   * place: `session` goes to the 21 back-office operations as a bearer token,
   * `terminalLease` to the two terminal operations in its own header, and the
   * three public operations get neither. A terminal holds both at once, so
   * "send whatever we have" would send a device lease to the back-office and a
   * person's session to the terminal routes — the swap the document warns
   * about in as many words.
   *
   * Each provider is resolved per request, so a session issued after sign-in
   * or a lease that renewed mid-shift is picked up without rebuilding the
   * client. A provider returning `undefined` sends nothing rather than an
   * empty credential.
   */
  credentials?: CredentialSource;

  /** Injected so tests never touch the network. Defaults to global `fetch`. */
  fetch?: typeof globalThis.fetch;
}

/**
 * A body the document declares under a media type that is not JSON, read as
 * bytes: what `ApiClient.download` answers with.
 *
 * The blob rather than a string, because what these are for is being saved. A
 * CSV decoded into a JS string only to be re-encoded on the way to a file gains
 * nothing and loses the response's own charset.
 */
export interface DownloadedFile {
  readonly blob: Blob;

  /** The response's `Content-Type`, verbatim — `text/csv; charset=utf-8`. */
  readonly mediaType: string;

  /**
   * The name from `Content-Disposition`, or `undefined` where the header did
   * not arrive.
   *
   * **A name is the ordinary answer**, including cross-origin. The header is not
   * CORS-safelisted, so a browser withholds it from JavaScript unless the server
   * names it in `Access-Control-Expose-Headers` — which `main.ts` does, alongside
   * `x-request-id`, since their #406. That matters to both of this app's targets,
   * because both are cross-origin to the API: the back office is a browser on
   * another port, and an Electron renderer sends `app://blossom` as its `Origin`.
   *
   * Still optional, because exposed is not the same as guaranteed — a proxy in
   * front of the API can strip a response header. A caller wanting a file on
   * disk needs a default for that, not a rebuilt copy of the server's naming.
   */
  readonly filename: string | undefined;
}

export interface ApiClient {
  request<Id extends OperationId>(
    operation: OperationDescriptor<Id>,
    ...args: RequestArgs<Id>
  ): Promise<OperationResponse<Id>>;

  /**
   * Fetch an operation whose success body is **not** JSON, as bytes.
   *
   * Restricted by its type to the operations the document declares that way —
   * the two CSV exports — so it cannot be pointed at an ordinary read, and an
   * ordinary read cannot be pointed at `request()` and end in `JSON.parse` on a
   * CSV.
   *
   * Everything about the *request* is `request()`'s, through the same code: the
   * URL from the descriptor, the credential chosen from the operation's own
   * `security`, the caller's credential-shaped headers stripped first, and
   * `redirect: 'error'`. A refusal is the same `ApiError` carrying the same
   * `code`, because both routes decide every refusal before their first byte
   * and answer the ordinary envelope. Only the reading of a **successful** body
   * differs.
   *
   * A second method rather than a flag on `request()`, because the return type
   * is genuinely different and `OperationResponse` for these two is `void` —
   * a flag would need a conditional return that lies for every other operation.
   */
  download<Id extends NonJsonOperationId>(
    operation: OperationDescriptor<Id>,
    ...args: DownloadArgs<Id>
  ): Promise<DownloadedFile>;
}

/**
 * What a path or query parameter may be on the wire. Narrower than `unknown`
 * on purpose: an object stringifies to `[object Object]`, which produces a URL
 * that is wrong in a way no one notices until the API 404s.
 */
type ParamPrimitive = string | number | boolean;
type ParamValue = ParamPrimitive | readonly ParamPrimitive[] | null | undefined;

interface RequestInput {
  path?: Record<string, ParamValue>;
  query?: Record<string, ParamValue>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  onStatus?: (status: number) => void;
}

/**
 * `/v1/users/{id}` plus `{ id: 'a/b' }` becomes `/v1/users/a%2Fb`.
 *
 * The name pattern is "anything that is not a brace or a slash" rather than
 * `\w+`, because OpenAPI permits `{outlet-id}` and `\w` does not match a
 * hyphen. Under `\w+` such a template passed through un-interpolated and the
 * client requested a literal `%7Boutlet-id%7D` — a 404 naming nothing. No path
 * in the current document uses one; this costs nothing and removes the trap.
 *
 * Exported from the package entry point for the one caller that cannot go
 * through `request()`: the terminal's outbox queues a write hours before it is
 * sent, so it stores a *resolved* pathname rather than an operation call. The
 * alternative is a second interpolator in `apps/desktop`, which is how one of
 * them eventually stops encoding a segment the other does — and this is the
 * function that turns a template into the URL a device's lease is sent to.
 * Anything that *can* call `request()` should, and gets this applied for it.
 */
export function interpolate(
  template: string,
  params: Record<string, ParamValue> | undefined,
): string {
  return template.replace(/\{([^{}/]+)\}/g, (_match, name: string) => {
    const value = params?.[name];

    // A blank id would silently address the collection endpoint instead — a
    // GET that returns every user where the caller asked for one.
    if (value === undefined || value === null || value === '') {
      throw new Error(`Path parameter "${name}" is required by ${template} but was not supplied.`);
    }

    return encodeURIComponent(String(value));
  });
}

function queryString(query: Record<string, ParamValue> | undefined): string {
  if (!query) return '';

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    // An omitted filter and a filter set to nothing are the same request, and
    // `?role=undefined` is neither.
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }

  const serialised = params.toString();

  return serialised ? `?${serialised}` : '';
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ErrorEnvelope).code === 'string' &&
    typeof (value as ErrorEnvelope).statusCode === 'number'
  );
}

async function readEnvelope(response: Response): Promise<ErrorEnvelope | undefined> {
  try {
    const body: unknown = await response.json();

    return isErrorEnvelope(body) ? body : undefined;
  } catch {
    // A gateway's HTML, or an empty body. There is nothing to branch on.
    return undefined;
  }
}

const resolveHeaders = async (source: HeaderSource | undefined): Promise<Record<string, string>> =>
  typeof source === 'function' ? source() : (source ?? {});

/**
 * Later sources win, and every name is lower-cased so they win by replacement.
 *
 * `Headers` folds case by *combining* values rather than overwriting them, so
 * `{ Authorization: 'Bearer stale' }` merged naively with the selected
 * `authorization` would be sent as "Bearer stale, Bearer fresh" — a credential
 * the API rejects, for a reason no one reading either object would guess.
 */
function mergeHeaders(...sources: (Record<string, string> | undefined)[]): Record<string, string> {
  const merged: Record<string, string> = {};

  for (const source of sources) {
    for (const [name, value] of Object.entries(source ?? {})) {
      merged[name.toLowerCase()] = value;
    }
  }

  return merged;
}

/**
 * The filename out of a `Content-Disposition`, or `undefined`.
 *
 * `filename*` (RFC 5987) is preferred where both are present, which is the rule
 * the header itself sets. Neither export produces one today — every character of
 * their filenames is `YYYY-MM-DD` or a hyphen — but reading the plain form only
 * would silently take the wrong half the day one does.
 */
export function filenameFromContentDisposition(header: string | null): string | undefined {
  if (header === null) return undefined;

  const extended = /filename\*\s*=\s*[\w-]*'[^']*'([^;]+)/i.exec(header);

  if (extended?.[1] !== undefined) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // A malformed percent-escape. Fall through to the plain form rather than
      // throwing: a filename is not worth failing a download that arrived.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|([^;]+))/i.exec(header);
  const value = plain?.[2] ?? plain?.[3];

  return value === undefined || value.trim() === '' ? undefined : value.trim();
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/+$/, '');
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  /**
   * Everything both readers share: build the URL, choose the credential, send,
   * and turn a refusal into `ApiError`.
   *
   * One function rather than two because the parts that must not differ are the
   * security-relevant ones — the credential-shaped headers stripped from the
   * caller's layers, the scheme picked from the operation's own `security`, and
   * `redirect: 'error'`. Two copies of this is how the export route eventually
   * stops stripping a parked lease that the read route still strips.
   *
   * Resolves the raw `Response` on success, with the URL it was fetched from —
   * both readers name it in their failure messages, and building it a second
   * time at each of them is how one eventually names a URL that was not the one
   * requested. What to do with the body is the caller's, and it is the only
   * thing the two readers decide differently.
   */
  async function send(
    operation: OperationDescriptor,
    input: RequestInput,
  ): Promise<{ response: Response; url: string }> {
    const url = `${baseUrl}${interpolate(operation.path, input.path)}${queryString(input.query)}`;

    // A credential enters from `credentials` and nowhere else. Every header
    // one could occupy is stripped from the caller's layers first — going on
    // last only wins the names the selected credential actually uses, and a
    // lease parked under its own name survived every request that must never
    // carry it. Names are already lower-cased by the merge, so `Authorization`
    // is dropped alongside `authorization`.
    const supplied = Object.fromEntries(
      Object.entries(mergeHeaders(await resolveHeaders(options.headers), input.headers)).filter(
        ([name]) => !credentialHeaderNames.has(name),
      ),
    );

    const headers = mergeHeaders(
      supplied,
      await credentialHeadersFor(operation, options.credentials),
    );

    // A non-JSON body (`text/csv`) goes as the caller's bytes, untouched: the
    // machine export's BOM and line endings are part of what the API hashes and
    // stores. Everything else about the request is the same path as JSON.
    const hasBody = input.body !== undefined;
    const raw = operation.bodyMediaType !== undefined;
    if (hasBody) headers['content-type'] = operation.bodyMediaType ?? 'application/json';

    let response: Response;
    try {
      response = await doFetch(url, {
        method: operation.method.toUpperCase(),
        headers,
        body: !hasBody ? undefined : raw ? (input.body as RawBody) : JSON.stringify(input.body),
        signal: input.signal,

        // A cross-origin redirect strips Authorization, Cookie and
        // Proxy-Authorization — and nothing else. `x-terminal-lease` is a
        // custom header, so under the default `follow` it survives the hop
        // and is handed to whatever host the API redirects to. That lease
        // authenticates a device, renews itself, and unlocks the roster with
        // every staff PIN verifier on it.
        //
        // No operation in the document legitimately redirects. Refusing rejects
        // before the second origin is ever contacted, and the rejection is not
        // an AbortError, so it falls through to ApiNetworkError below — which
        // is the right word for it, since a redirecting API host is a captive
        // portal or a hostile proxy.
        redirect: 'error',
      });
    } catch (cause) {
      // An abort is the caller's own doing — a screen that unmounted, a
      // superseded search — and must not be reported as the network being
      // down.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;

      throw new ApiNetworkError(`Could not reach ${url}.`, { cause });
    }

    // A refusal is the ordinary envelope whatever the success body would have
    // been: both CSV exports decide every refusal before their first byte, so
    // an export that fails fails exactly the way a read does, with a `code` to
    // branch on. After the first byte the status line is already sent and there
    // is no envelope left — that arrives as a truncated body with no totals
    // row, which is the export's own completeness marker rather than anything
    // this layer can see.
    if (!response.ok) {
      throw new ApiError(response.status, await readEnvelope(response));
    }

    return { response, url };
  }

  return {
    async request<Id extends OperationId>(
      operation: OperationDescriptor<Id>,
      ...args: RequestArgs<Id>
    ): Promise<OperationResponse<Id>> {
      const input = (args[0] ?? {}) as RequestInput;

      const { response, url } = await send(operation, input);

      // Success, and the status is now known. Reported before anything is read
      // so that the eight writes answering both 200 and 201 can tell "recorded"
      // from "already recorded" — the answer is the original outcome either way,
      // so the status is what a caller has. The eight are listed, with how the
      // list was derived, on `RequestExtras.onStatus`.
      input.onStatus?.(response.status);

      // A 204 declares there is no body. `OperationResponse` is `void` for
      // every operation that answers one, so this cast is honest.
      if (response.status === 204) return undefined as OperationResponse<Id>;

      let text: string;
      try {
        text = await response.text();
      } catch (cause) {
        throw new ApiNetworkError(`The response from ${url} could not be read to the end.`, {
          cause,
        });
      }

      // An empty body is the same statement a 204 makes, and the operations
      // that answer it declare no content in the document.
      if (text === '') return undefined as OperationResponse<Id>;

      try {
        return JSON.parse(text) as OperationResponse<Id>;
      } catch (cause) {
        // Previously this returned `undefined` typed as the operation's body,
        // which compiles and then throws a TypeError inside render — three
        // frames from the cause, mid-queue, with nothing naming what happened.
        // A caller cannot consume this response, so say so here.
        //
        // This is reached only for an operation whose success the document
        // declares as JSON. The two that declare CSV are `download`'s, and are
        // kept off this path by `NonJsonOperationId` rather than by a caller
        // remembering — before that they would have arrived here and been
        // reported as a captive portal for answering exactly what they promise.
        throw new ApiNetworkError(
          `${url} answered ${response.status} with a body that is not JSON. ` +
            'Something is answering in the API’s place — a captive portal, a proxy, ' +
            'or a connection cut mid-response.',
          { cause },
        );
      }
    },

    async download<Id extends NonJsonOperationId>(
      operation: OperationDescriptor<Id>,
      ...args: DownloadArgs<Id>
    ): Promise<DownloadedFile> {
      const input = (args[0] ?? {}) as RequestInput;

      const { response, url } = await send(operation, input);

      let blob: Blob;
      try {
        blob = await response.blob();
      } catch (cause) {
        // A stream cut mid-body. Worth its own sentence rather than a partial
        // file: these two are financial exports whose last line is what says
        // they are complete, and half a cash book that saved without complaint
        // is the worst failure this feature has.
        throw new ApiNetworkError(`The export from ${url} could not be read to the end.`, {
          cause,
        });
      }

      return {
        blob,
        // `blob.type` is already lower-cased and stripped by some
        // implementations; the header is what the server actually said.
        mediaType: response.headers.get('content-type') ?? blob.type,
        filename: filenameFromContentDisposition(response.headers.get('content-disposition')),
      };
    },
  };
}
